import React, { useState } from 'react';
import { X, Crown, CreditCard, AlertCircle, Check } from 'lucide-react';
import { createCheckoutSession, createCustomerPortalSession, ensureStripeCustomer } from '../../services/stripeService';
import { SUBSCRIPTION_TIERS } from '../../services/subscriptionService';

const SubscriptionManagementModal = ({ isOpen, onClose, userSubscription, onStartTrial, currentUser, refreshSubscription = () => {}, hasUsedTrial: hasUsedTrialProp = false }) => {
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  if (!isOpen) return null;

  const trialEndDate = userSubscription?.trialEndDate
    ? (userSubscription.trialEndDate.toDate
      ? userSubscription.trialEndDate.toDate()
      : new Date(userSubscription.trialEndDate))
    : null;

  const trialEnded = trialEndDate ? trialEndDate <= new Date() : false;

  const subscriptionStatus = (userSubscription?.subscriptionStatus || '').toLowerCase();
  const isTrialActive = subscriptionStatus === 'trial' && !trialEnded;
  const isExpired = subscriptionStatus === 'expired' || (subscriptionStatus === 'trial' && trialEnded);
  const isPremium = subscriptionStatus === 'active';
  const hasBillingIssue = ['past_due', 'unpaid', 'incomplete'].includes(subscriptionStatus);
  const hasUsedTrial = !isTrialActive && (hasUsedTrialProp || isExpired || userSubscription?.trialUsed || trialEnded);
  const couponDiscount = userSubscription?.couponDiscountPercent || 0;
  const basePrice = SUBSCRIPTION_TIERS.PREMIUM.price;
  const discountedPrice = couponDiscount ? (basePrice * (1 - couponDiscount / 100)).toFixed(2) : basePrice.toFixed(2);
  const userId = currentUser?.uid || currentUser?.id || null;
  const cancelAtPeriodEnd = userSubscription?.subscription?.cancelAtPeriodEnd ?? userSubscription?.cancelAtPeriodEnd ?? false;
  const rawPeriodEnd = userSubscription?.subscription?.currentPeriodEnd
    || userSubscription?.subscriptionCurrentPeriodEnd
    || userSubscription?.subscriptionNextRenewal;
  const rawNextBillingDate = userSubscription?.subscription?.nextBillingDate || userSubscription?.nextBillingDate;
  const periodEndDate = rawPeriodEnd
    ? (rawPeriodEnd.toDate ? rawPeriodEnd.toDate() : new Date(rawPeriodEnd))
    : null;
  const nextBillingDate = rawNextBillingDate
    ? (rawNextBillingDate.toDate ? rawNextBillingDate.toDate() : new Date(rawNextBillingDate))
    : null;
  const premiumRenewalDate = cancelAtPeriodEnd ? periodEndDate : nextBillingDate || periodEndDate;
  const daysRemaining = trialEndDate ? Math.ceil((trialEndDate - new Date()) / (1000 * 60 * 60 * 24)) : 0;

  const handleStartTrial = async () => {
    if (hasUsedTrial) {
      await handleStartStripeCheckout();
      return;
    }
    try {
      setLoading(true);
      await onStartTrial();
      onClose();
    } catch (error) {
      console.error('Error starting trial:', error);
      alert('Failed to start trial. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStartStripeCheckout = async () => {
    if (!userId || !currentUser?.email) {
      alert('Please sign in to upgrade and ensure your email is on file.');
      return;
    }
    try {
      setCheckoutLoading(true);
      await ensureStripeCustomer(userId, currentUser.email, currentUser.displayName || currentUser.name);
      await createCheckoutSession(userId, 'premium', currentUser.email);
    } catch (error) {
      console.error('Error starting Stripe checkout:', error);
      alert('Unable to start checkout. Please try again or contact support.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    if (!userId || !currentUser?.email) {
      alert('Please sign in to manage billing.');
      return;
    }
    try {
      setPortalLoading(true);
      await ensureStripeCustomer(userId, currentUser.email, currentUser.displayName || currentUser.name);
      await createCustomerPortalSession(userId);
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Unable to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const renderTrialManagement = () => (
    <div className="p-6">
      {/* Trial Status */}
      <div className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-xl p-6 mb-6 shadow-inner">
        <div className="flex items-center gap-3 mb-4">
          <Crown className="w-6 h-6 text-[color:var(--color-text)]" />
          <h3 className="text-xl font-bold text-[color:var(--color-text)]">Premium Trial Active</h3>
        </div>
        <div className="grid grid-cols-2 gap-4 text-[color:var(--color-text)]">
          <div>
            <p className="text-sm opacity-80">Days Remaining</p>
            <p className="text-2xl font-bold">{daysRemaining}</p>
          </div>
          <div>
            <p className="text-sm opacity-80">Trial Ends</p>
            <p className="font-semibold">{trialEndDate?.toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Payment & Billing Section */}
      <div className="bg-gray-50 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <CreditCard className="w-5 h-5 text-gray-600" />
          <h4 className="font-semibold text-gray-900">Payment & Billing</h4>
        </div>
        <div className="space-y-3">
          <p className="text-sm text-gray-700">
            Manage billing securely through Stripe: add/update cards, view invoices, change plans, or add a promo code for future payments.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleStartStripeCheckout}
              disabled={checkoutLoading || isPremium}
              className="flex-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] py-2 px-4 rounded-lg font-medium hover:brightness-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {checkoutLoading ? 'Opening Checkout...' : isPremium ? 'Subscription Active' : 'Upgrade with Stripe'}
            </button>
            <button
              onClick={handleOpenBillingPortal}
              disabled={portalLoading}
              className="flex-1 bg-white text-[color:var(--color-text)] border border-gray-300 py-2 px-4 rounded-lg font-medium hover:brightness-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {portalLoading ? 'Opening Portal...' : 'Manage Billing / Promo Code'}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            You will be redirected to Stripe to add/update cards, handle invoices, add a promo code, or cancel.
          </p>
        </div>
      </div>

      {/* Auto-Renewal Settings */}
      <div className="rounded-xl p-6 mb-6 bg-info-gradient">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-[color:var(--color-text)] mt-0.5" />
          <div>
            <h4 className="font-semibold text-[color:var(--color-text)] mb-2">Auto-Renewal</h4>
            <p className="text-[color:var(--color-text)] text-sm mb-3">
              No charges occur until you add a payment method. After your 14-day trial, you'll be downgraded to the Free plan unless a card is on file. If a card is added, billing resumes at €{couponDiscount ? discountedPrice : basePrice.toFixed(2)} per month (current coupon applies if present). Failed or cancelled payments also return you to Free—your data stays, but premium features lock until you upgrade again.
            </p>
          </div>
        </div>
      </div>

      {/* Premium Features */}
      <div className="bg-white border rounded-xl p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Your Premium Features</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            'Receipt Scanner (snap & upload expenses)',
            'Voice Input. Say “Nails €50” or “Products €22” — done',
            'Smart auto-fill',
            'Real-time tax estimate. See how much tax you’ll owe',
            'Pricing Calculator. Know how much you should charge',
            'Overhead Calculator. Know what your time costs per hour',
            'Profit per service. See which services are most profitable',
            'Full business analytics dashboard'
          ].map((feature, index) => (
            <div key={index} className="flex items-center gap-3">
              <Check className="w-4 h-4 text-[#4FB368]" />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderPremiumManagement = () => {
    const renewalLabel = cancelAtPeriodEnd ? 'Ends on' : 'Next renewal';
    const renewalDateLabel = premiumRenewalDate ? premiumRenewalDate.toLocaleDateString() : '—';
    const priceLabel = couponDiscount
      ? `€${discountedPrice} / month (after ${couponDiscount}% off)`
      : `€${basePrice.toFixed(2)} / month`;

    return (
      <div className="p-6">
        {/* Premium Status */}
        <div className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-xl p-6 mb-6 shadow-inner">
          <div className="flex items-start gap-3 mb-3">
            <Crown className="w-6 h-6 text-[color:var(--color-text)] mt-1" />
            <div>
              <h3 className="text-xl font-bold text-[color:var(--color-text)]">Premium Active</h3>
              <p className="text-sm opacity-80">
                {cancelAtPeriodEnd
                  ? `You cancelled auto-renewal. Premium stays active until ${renewalDateLabel}.`
                  : 'Your plan renews monthly. Manage or cancel anytime in Stripe.'}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-[color:var(--color-text)]">
            <div>
              <p className="text-sm opacity-80">Plan</p>
              <p className="text-2xl font-bold">Premium</p>
              <p className="text-sm opacity-80">{priceLabel}</p>
            </div>
            <div>
              <p className="text-sm opacity-80">{renewalLabel}</p>
              <p className="font-semibold">{renewalDateLabel}</p>
              {cancelAtPeriodEnd && (
                <p className="text-xs opacity-80 mt-1">Billing stops after this date unless you resume.</p>
              )}
            </div>
          </div>
        </div>

        {/* Payment & Billing Section */}
        <div className="bg-gray-50 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-gray-600" />
            <h4 className="font-semibold text-gray-900">Payment & Billing</h4>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              Update cards, view invoices, change plans, or undo cancellation securely in Stripe.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleOpenBillingPortal}
                disabled={portalLoading}
                className="flex-1 bg-[color:var(--color-warn)] text-[color:var(--color-text)] py-2 px-4 rounded-lg font-medium hover:brightness-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {portalLoading ? 'Opening Portal...' : cancelAtPeriodEnd ? 'Manage / Resume in Stripe' : 'Manage Billing in Stripe'}
              </button>
              <button
                onClick={refreshSubscription}
                className="flex-1 bg-white text-[color:var(--color-text)] border border-gray-300 py-2 px-4 rounded-lg font-medium hover:brightness-95 transition"
              >
                Refresh status
              </button>
            </div>
            {cancelAtPeriodEnd && (
              <p className="text-xs text-gray-500">
                You keep Premium access until {renewalDateLabel}. Open Stripe to resume renewal or update payment details.
              </p>
            )}
          </div>
        </div>

        {/* Premium Features */}
        <div className="bg-white border rounded-xl p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Your Premium Features</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              'Receipt Scanner (snap & upload expenses)',
              'Voice Input. Say "Nails €50" or "Products €22" - done',
              'Smart auto-fill',
              "Real-time tax estimate. See how much you'll owe",
              'Pricing Calculator. Know how much you should charge',
              'Overhead Calculator. Know what your time costs per hour',
              'Profit per service. See which services are most profitable',
              'Full business analytics dashboard'
            ].map((feature, index) => (
              <div key={index} className="flex items-center gap-3">
                <Check className="w-4 h-4 text-[#4FB368]" />
                <span className="text-gray-700 text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderFreeUserContent = () => (
    <div className="p-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Crown className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Upgrade to Premium</h3>
        <p className="text-gray-600">
          Unlock powerful features to streamline your business finances
        </p>
      </div>

        <div className="rounded-xl p-6 mb-6 border border-[color:var(--color-info)] bg-[color:var(--color-info)]/60">
          <div className="text-center mb-4">
            <h4 className="text-xl font-bold text-gray-900 mb-1">Premium</h4>
            <div className="text-3xl font-bold text-gray-900">
              {couponDiscount ? (
                <>
                  <span className="line-through text-gray-500 mr-2">€{basePrice.toFixed(2)}</span>
                  €{discountedPrice}
                </>
              ) : (
                <>€{basePrice.toFixed(2)}</>
              )}
            </div>
            <div className="text-gray-600">
              per month {couponDiscount ? `(with ${couponDiscount}% off for life)` : ''}
            </div>
            {!hasUsedTrial ? (
              <div className="text-sm text-[color:var(--color-text)] font-medium mt-2">
                14-day free trial
              </div>
            ) : (
              <div className="text-sm text-[color:var(--color-text)] font-medium mt-2">
                Trial used — upgrade to keep premium features
              </div>
            )}
          </div>

        <button
          onClick={hasUsedTrial ? handleStartStripeCheckout : handleStartTrial}
          disabled={loading || checkoutLoading}
          className="w-full py-3 px-4 bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded-lg font-medium hover:brightness-95 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mb-4"
        >
          {loading || checkoutLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-[color:var(--color-text)] border-t-transparent rounded-full animate-spin"></div>
              {hasUsedTrial ? 'Opening Checkout...' : 'Starting Trial...'}
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" />
              {hasUsedTrial ? 'Upgrade to Premium' : 'Upgrade to Premium'}
            </>
          )}
        </button>

        <div className="grid grid-cols-1 gap-2">
          {[
            "Talk instead of typing - Add income or expenses just by speaking",
            "Snap & save receipts - Take a picture, and we'll fill in the details for you",
            "See your money clearly - Simple charts that show where your money goes",
            "Track growth easily - Understand what's working in your business"
          ].map((feature, index) => (
            <div key={index} className="flex items-center gap-3">
              <Check className="w-4 h-4 text-[color:var(--color-primary)]" />
              <span className="text-gray-700 text-sm">{feature}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderBillingIssueContent = () => {
    const statusLabel = userSubscription?.subscriptionStatus === 'past_due'
      ? 'Payment overdue'
      : userSubscription?.subscriptionStatus === 'unpaid'
        ? 'Payment unpaid'
        : 'Payment incomplete';

    return (
      <div className="p-6">
        <div className="rounded-xl p-6 mb-6 border border-[color:var(--color-warn)] bg-[color:var(--color-warn)]/30">
          <div className="flex items-center gap-3 mb-3 text-[color:var(--color-text)]">
            <AlertCircle className="w-6 h-6" />
            <h3 className="text-xl font-bold">{statusLabel}</h3>
          </div>
          <p className="text-[color:var(--color-text)] mb-4">
            Your Premium subscription exists in Stripe, but payment needs attention. To avoid being charged twice,
            manage billing in Stripe to pay any outstanding invoice and/or update your payment method.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleOpenBillingPortal}
              disabled={portalLoading}
              className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] py-2 px-4 rounded-lg font-medium hover:brightness-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {portalLoading ? 'Opening Portal...' : 'Manage Billing (Stripe)'}
            </button>
            <button
              onClick={onClose}
              className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition"
            >
              Close
            </button>
          </div>
        </div>

        <div className="bg-white border rounded-xl p-6">
          <h4 className="font-semibold text-gray-900 mb-4">What happens next</h4>
          <div className="space-y-3">
            {[
              'Pay any outstanding invoice (if shown)',
              'Update your card details if needed',
              'Return to the app and refresh to unlock Premium features again'
            ].map((item) => (
              <div key={item} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-[#4FB368] mt-0.5" />
                <span className="text-gray-700 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };


  const renderExpiredContent = () => (
    <div className="p-6">
      <div className="rounded-xl p-6 mb-6 border border-[color:var(--color-info)] bg-[color:var(--color-info)]/60">
        <div className="flex items-center gap-3 mb-4 text-[color:var(--color-text)]">
          <AlertCircle className="w-6 h-6" />
          <h3 className="text-xl font-bold">Trial Expired</h3>
        </div>
        <p className="text-[color:var(--color-text)] mb-4">
          Your 14-day premium trial has ended. Upgrade to keep using premium features.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleStartStripeCheckout}
            disabled={checkoutLoading}
            className="bg-[color:var(--color-warn)] text-[color:var(--color-text)] py-2 px-4 rounded-lg font-medium hover:brightness-95 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {checkoutLoading ? 'Opening Checkout...' : 'Upgrade to Premium'}
          </button>
          <button
            onClick={onClose}
            className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );

  const getModalTitle = () => {
    if (isTrialActive) return 'Manage Trial';
    if (isExpired) return 'Trial Expired';
    if (isPremium) return cancelAtPeriodEnd ? 'Premium Ending' : 'Premium Active';
    if (hasBillingIssue) return 'Payment Required';
    return 'Upgrade to Premium';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">{getModalTitle()}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        {isTrialActive ? renderTrialManagement()
          : isExpired ? renderExpiredContent()
          : isPremium ? renderPremiumManagement()
          : hasBillingIssue ? renderBillingIssueContent()
          : renderFreeUserContent()
        }
      </div>
    </div>
  );
};

export default SubscriptionManagementModal;
